import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { Input, CardActions, Switch, Checkbox } from 'react-toolbox';
import Card from 'components/Card';
import cx from 'classnames';
import MessageBox from 'components/MessageBox';
import PhoneInput from 'components/PhoneType/PhoneInput';
import ColoredButton from 'components/ColoredButton';
import BreakLine from 'components/BreakLine';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import styles from 'styles/form';
import RTInput from 'styles/input';
import RTDropdown from 'styles/dropdown';
import RTCard from 'styles/card';
import PhoneType from 'components/PhoneType';
import { error, generateSource } from '../../../../../utils';
import RTSwitch from 'styles/switch';
import ReactSelect from 'components/StyledSelect/react_select';
import DatePicker from 'components/DatePicker';
import reactSelectStyles from 'components/ReactSelect/styles';
import { handleDateSave } from '../../../../../utils/dateUtils.js';
import EmployeeType from 'types/employee';
import RoleSelect from 'components/formElements/RoleSelect';
import { TIMEZONES, EMAIL_VALIDATION_REGEX, VENDOR_NAME_MAP, DEFAULT_MVW } from 'config/constants';
import { cloneDeep, capitalize } from 'lodash';
import RTCheckbox from 'styles/checkbox.scss';
import { can } from '../../../../../services/ability';

const theme = { RTInput, RTDropdown, RTCard, RTSwitch };
const mediumInput = cx({
  [styles.inputs]: true,
  [styles.largeInput]: true,
});
const cancelClass = cx([styles.cancelButton, styles.actionButton]);
const TIMEZONE_SOURCE = TIMEZONES.map(tz => ({ value: tz, label: tz }));

class EmployeeForm extends Component {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleActive = this.handleChange.bind(this, 'active');
    this.handleRole = this.handleChange.bind(this, 'role');
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSelectChange = (key, value) => this.handleChange(key, value.map(v => v.value));
    this.handlePrimaryType = this.handleChange.bind(this, 'primary_phone_type');
    this.handleSecondaryType = this.handleChange.bind(this, 'secondary_phone_type');
    this.handleName = this.handleChange.bind(this, 'name');
    this.handlePosition = this.handleChange.bind(this, 'position');
    this.handleEmail = this.handleChange.bind(this, 'email');
    this.handleSecondaryPhone = this.handleChange.bind(this, 'secondary_phone');
    this.handlePrimaryPhone = this.handleChange.bind(this, 'primary_phone');
    this.handleLeadCertNumber = this.handleChange.bind(this, 'lead_cert_number');
    this.handleBadgeNumber = this.handleChange.bind(this, 'number');
    this.handleBadgeExpiration = this.handleChange.bind(this, 'expiration');
    this.handleWorkrooms = this.handleSelectChange.bind(this, 'workrooms');
    this.handleAssignable = this.handleChange.bind(this, 'assignable');
    this.handleContractorTableApproved = this.handleChange.bind(this, 'contractor_table_approved');
    this.handleCanDeliver = this.handleChange.bind(this, 'can_deliver');
    this.handleTimezone = this.handleChange.bind(this, 'timezone');
    this.handleLrrpDateExpiration = this.handleChange.bind(this, 'lrrp_date_expiration');
    this.handleLhi = this.handleChange.bind(this, 'lhi');
    this.handleRf = this.handleChange.bind(this, 'rf');
    this.handleFd = this.handleChange.bind(this, 'fd');
    this.handleIfi = this.handleChange.bind(this, 'ifi');
    this.handleAh = this.handleChange.bind(this, 'ah');
    this.handleBj = this.handleChange.bind(this, 'bj');
    this.handleSam = this.handleChange.bind(this, 'sam');
    this.handleWay = this.handleChange.bind(this, 'way');

    const employee = cloneDeep(this.props.employee);
    const workrooms = (employee && employee.workrooms.map(w => w.id)) || [];
    this.state = {
      invalidEmail: false,
      employee: (employee && { ...employee, workrooms }) || {
        active: true,
        assignable: false,
        contractor_table_approved: false,
        primary_phone_type: 'mobile',
        secondary_phone_type: 'mobile',
        badge: {},
        manage_vendor_workrooms: cloneDeep(DEFAULT_MVW),
        workrooms,
      },
    };
  }

  handleChange = (key, value) => {
    const { updateDiff = null } = this.props;
    const employee = this.state.employee;
    const emailChange = key === 'email';
    switch (key) {
      case 'number':
      case 'expiration':
        employee.badge = employee.badge || {};
        employee.badge[key] = value;
        if (updateDiff) updateDiff(key, value, 'badge');
        break;
      case 'timezone':
        employee.timezone = value.value;
        if (updateDiff) updateDiff('timezone', value.value);
        break;
      case 'lhi':
      case 'rf':
      case 'fd':
      case 'ifi':
      case 'ah':
      case 'bj':
      case 'sam':
      case 'way':
        employee.manage_vendor_workrooms[key] = value;
        if (updateDiff) updateDiff(key, value, 'manage_vendor_workrooms');
        break;
      default:
        if (updateDiff) updateDiff(key, value);
        employee[key] = emailChange ? value.replaceAll(' ', '') : value;
    }
    this.setState({ employee, invalidEmail: emailChange && !this.validateEmail(value) });
  }

  handleCancel = () => this.props.onCancel();

  handleSave = () => {
    const { badge, ...user } = this.state.employee;
    const { position, workrooms, role, id, user_id, assignable,
      contractor_table_approved, lrrp_date_expiration, can_deliver,
      manage_vendor_workrooms = { ...DEFAULT_MVW }, ...userAttributes } = user;
    const newBadge = badge || {};
    const { expiration, ...otherBadgeThings } = newBadge;
    this.props.onSave({
      position,
      id,
      role,
      assignable,
      contractor_table_approved,
      can_deliver,
      lrrp_date_expiration: handleDateSave(lrrp_date_expiration),
      manage_vendor_workrooms,
      user_attributes: {
        ...userAttributes,
        id: user_id,
        workroom_ids: workrooms,
        badge_attributes: {
          ...otherBadgeThings,
          expiration: handleDateSave(expiration),
        },
      },
    });
  }

  validateEmail = email => EMAIL_VALIDATION_REGEX.test(email);

  filterWorkroomOptionsByVendor = (workrooms, mvw) => {
    if (!Object.keys(mvw).length) return [];
    return workrooms.filter(w => mvw[w.vendor.toLowerCase()] === true);
  }

  renderTitle = (employee, isEdit) => {
    const title = isEdit ? (employee.name || 'Edit Employee') : 'Add Employee';
    const switchClassNames = cx({
      [styles.switchTitleAdd]: !isEdit,
      [styles.switchTitle]: true,
    });
    return (<div>
      <FlexBox justifyContent="space-between" className={styles.divider}>
        <div>
          <h5 className={styles.cardTitle}>{title}</h5>
          <p className={styles.cardSubtitle}>
            Please ensure that all fields contain accurate information
          </p>
        </div>
        <div>
          <Switch
            name="active"
            label="Account Active"
            onChange={this.handleActive}
            className={switchClassNames}
            checked={employee.active}
          />
          <Switch
            name="assignable"
            label="Can Be Assigned"
            onChange={this.handleAssignable}
            className={switchClassNames}
            checked={employee.assignable}
          />
          <Switch
            name="contractor_table_approved"
            label="Contractor Table Approved"
            onChange={this.handleContractorTableApproved}
            className={switchClassNames}
            checked={employee.contractor_table_approved}
          />
          <Switch
            name="can_deliver"
            label="Can Deliver"
            onChange={this.handleCanDeliver}
            className={switchClassNames}
            checked={employee.can_deliver}
          />
        </div>
      </FlexBox>
    </div>);
  }

  renderVendorSection = mvw => <Fragment>
    <BreakLine />
    {/* Until we make PFT its own vendor, we have to make special consideration */}
    {/* TODO: Remove this function when PFT is a vendor */}
    {Object.keys(VENDOR_NAME_MAP).filter(k => k !== 'PFT')
      .map(k => (<Checkbox
        key={k}
        checked={mvw[k.toLowerCase()] || false}
        label={`Manage ${VENDOR_NAME_MAP[k]}`}
        onChange={this[`handle${capitalize(k)}`]}
        className={styles.checkboxListItem}
        theme={RTCheckbox}
      />))}
  </Fragment>;

  render = () => {
    const { employee = null, invalidEmail } = this.state;
    const { errors, user, workrooms = null } = this.props;
    const isEdit = !!employee.id;
    const gError = error('global', errors);
    const badge = employee.badge || {};
    const workroomOptions = generateSource(
      this.filterWorkroomOptionsByVendor(workrooms, user.manage_vendor_workrooms),
    );
    const defaultMvw = (employee && typeof employee.manage_vendor_workrooms === 'object')
      ? employee.manage_vendor_workrooms : { ...DEFAULT_MVW };
    return (<ThemeProvider theme={theme}>
      <Card>
        {this.renderTitle(employee, isEdit)}
        {gError ? <MessageBox error message={gError} /> : null}
        <FlexBox flexWrap="wrap" className={styles.divider}>
          <Input
            label="Name"
            className={mediumInput}
            value={employee.name}
            onChange={this.handleName}
            error={error('user.name', errors)}
            required
          />
          <RoleSelect
            className={mediumInput}
            onChange={this.handleRole}
            value={employee.role}
            error={error('role', errors)}
            required
            simpleValue
          />
          <Input
            label="Position"
            value={employee.position}
            className={mediumInput}
            onChange={this.handlePosition}
            error={error('position', errors)}
            required
          />
          <ReactSelect
            className={mediumInput}
            label="Time Zone"
            placeholder={'Select Time Zone'}
            onChange={this.handleTimezone}
            options={TIMEZONE_SOURCE}
            value={employee.timezone}
            error={error('timezone', errors)}
            required
          />
          <Input
            label="Email"
            type="email"
            value={employee.email}
            className={mediumInput}
            onChange={this.handleEmail}
            error={invalidEmail ? 'Invalid Email' : error('user.email', errors)}
            required
          />
          <PhoneInput
            label="Primary Phone"
            type="tel"
            value={employee.primary_phone}
            className={mediumInput}
            onChange={this.handlePrimaryPhone}
            error={error('user.primary_phone', errors)}
          >
            <PhoneType value={employee.primary_phone_type} onChange={this.handlePrimaryType} />
          </PhoneInput>
          <PhoneInput
            label="Secondary Phone"
            type="tel"
            value={employee.secondary_phone}
            className={mediumInput}
            onChange={this.handleSecondaryPhone}
            error={error('user.secondary_phone', errors)}
          >
            <PhoneType
              value={employee.secondary_phone_type}
              onChange={this.handleSecondaryType}
            />
          </PhoneInput>
          {can(user.role, 'manage', 'manage_vendors') && this.renderVendorSection(defaultMvw)}
        </FlexBox>
        <FlexBox flexWrap="wrap" className={styles.divider}>
          <Input
            label="Badge number"
            className={mediumInput}
            value={badge.number || ''}
            onChange={this.handleBadgeNumber}
            error={error('user.badge.number', errors)}
          />
          <DatePicker
            label="Badge expiration"
            className={mediumInput}
            value={badge.expiration || ''}
            onChange={this.handleBadgeExpiration}
            error={error('user.badge.expiration', errors)}
          />
          <Input
            label="Lead Cert Number"
            className={mediumInput}
            value={employee.lead_cert_number}
            onChange={this.handleLeadCertNumber}
            error={error('user.lead_cert_number', errors)}
          />
          <DatePicker
            label="LRRP Training Expiration"
            className={mediumInput}
            value={employee.lrrp_date_expiration || ''}
            onChange={this.handleLrrpDateExpiration}
            error={error('user.lrrp_date_expiration', errors)}
          />
          <ReactSelect
            multi
            resetValue={[]}
            name="EmployeeWorkrooms"
            className={reactSelectStyles.selectWrapper}
            options={workroomOptions}
            value={employee.workrooms}
            onChange={this.handleWorkrooms}
            label="Assign workroom(s) to employee"
            placeholder="Select workroom(s)"
          />
        </FlexBox>
        <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <CardActions className={styles.actionsWrapper}>
            <ColoredButton
              className={cancelClass}
              responsive
              color="gray"
              label="Cancel"
              onClick={this.handleCancel}
            />
            <ColoredButton
              className={styles.actionButton}
              responsive
              color="green"
              label={isEdit ? 'Save Changes' : 'Add employee'}
              onClick={this.handleSave}
              disabled={invalidEmail}
            />
          </CardActions>
        </FlexBox>
      </Card>
    </ThemeProvider>);
  }
}

EmployeeForm.propTypes = {
  errors: PropTypes.object,
  employee: EmployeeType,
  workrooms: PropTypes.array,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  updateDiff: PropTypes.func,
  user: PropTypes.object,
};

export default EmployeeForm;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/employees/components/employeeForm.jsx